import React, { useState, useEffect } from 'react';
import { GlobalOutlined } from '@ant-design/icons';
import { Tooltip, Button, Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';

import i18n, { languages } from '@Shared/utils/i18n';
import useLocalStorage from '@Shared/utils/useLocalStorage';
import { Chevron } from '@Shared/ui';

import type { MenuProps } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';

// `textColor`: a plain globe, label and arrow in that colour instead of the bordered button (step 1, PandaDoc's way).
export default function LanguageDropdown({ textColor }: { textColor?: string } = {}) {
    const { t } = useTranslation();
    const storage = useLocalStorage();
    // The arrow points up while the menu is open, down otherwise.
    const [open, setOpen] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState<string>(storage.read('currentLanguage', i18n.language));
    const items: MenuProps['items'] = languages.map((l) => ({ key: l.key, label: <>{l.label}</> }));

    useEffect(() => {
        setTimeout(() => {
            i18n.changeLanguage(currentLanguage as string);
        }, 0);
    }, [currentLanguage]);

    const handleLanguageChange = (selected: ItemType) => {
        if (selected && selected.key) {
            storage.write('currentLanguage', selected.key as string);
            setCurrentLanguage(selected.key as string);
        }
    };

    return (
        <Dropdown
            trigger={['click']}
            onOpenChange={setOpen}
            // A plain list with hover only: the button already names the current language (Vova 29.10.2026).
            menu={{
                items,
                onClick: handleLanguageChange,
            }}
            placement={textColor ? 'top' : 'bottomRight'}
        >
            {textColor ? (
                <Button
                    type="text"
                    icon={<GlobalOutlined />}
                    style={{ color: textColor }}
                >
                    {languages.find((l) => l.key === currentLanguage)!.label}
                    <Chevron
                        size={14}
                        up={open}
                    />
                </Button>
            ) : (
                <Tooltip
                    title={t('signup.select_language')}
                    placement="left"
                >
                    <Button icon={<GlobalOutlined />}>{languages.find((l) => l.key === currentLanguage)!.label}</Button>
                </Tooltip>
            )}
        </Dropdown>
    );
}
